import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

// Ink-write reveal: words (or letters) ink themselves in one by one —
// soft blur → sharp, like ink soaking into parchment. Triggers in view.
interface InkTextProps {
  text: string;
  mode?: 'words' | 'letters';
  baseDelay?: number; // ms before the first token
  step?: number; // ms between tokens
  className?: string;
  style?: React.CSSProperties;
  as?: 'p' | 'blockquote';
}

const InkText: React.FC<InkTextProps> = ({
  text,
  mode = 'words',
  baseDelay = 100,
  step = 45,
  className = '',
  style,
  as = 'p',
}) => {
  const reduceMotion = useReducedMotion();
  const tokens = mode === 'letters' ? text.split('') : text.split(/(\s+)/);
  const Tag = as === 'blockquote' ? motion.blockquote : motion.p;

  let idx = 0;
  return (
    <Tag
      className={className}
      style={style}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '0px 0px -8% 0px' }}
    >
      {tokens.map((tok, i) => {
        if (!tok) return null;
        if (/^\s+$/.test(tok)) return <React.Fragment key={i}>{tok}</React.Fragment>;
        const delay = (baseDelay + idx * step) / 1000;
        idx++;
        return (
          <motion.span
            key={i}
            className="inline-block"
            variants={{
              hidden: reduceMotion
                ? { opacity: 1 }
                : { opacity: 0, y: '0.35em', filter: 'blur(3px)' },
              visible: {
                opacity: 1,
                y: 0,
                filter: 'blur(0px)',
                transition: {
                  duration: mode === 'letters' ? 0.4 : 0.7,
                  delay,
                  ease: [0.22, 0.61, 0.36, 1],
                },
              },
            }}
          >
            {tok}
          </motion.span>
        );
      })}
    </Tag>
  );
};

export default InkText;
